/**
 * Mirrors the keys of the API's CTA registry
 * (`prop-radar-api/src/modules/v1/cta/cta-registry.ts`).
 *
 * Only the keys live here. Display labels stay on the server and reach Super Admin via
 * `GET /super-admin/cta-analytics/registry`, so a newly added CTA appears in the
 * dashboard without a frontend release.
 *
 * Exported as constants rather than bare strings at each call site: a misspelled
 * `CTA_TYPES.USE_OFFR` is `undefined`, which `trackCta` reports loudly in development,
 * whereas a misspelled `"use_offr"` string would be silently dropped by the API and the
 * click would simply never appear on the dashboard.
 *
 * Adding a CTA means adding it to the API registry first — the server is the authority
 * and drops anything it does not recognise.
 */

export const CTA_TYPES = {
  USE_OFFER: "use_offer",
  BUY_CHALLENGE: "buy_challenge",
  PROMO_CODE_COPY: "promo_code_copy",
  PROMO_MODAL_VISIT_SITE: "promo_modal_visit_site",
  VISIT_FIRM_SITE: "visit_firm_site",
};

export const CTA_PLACEMENTS = {
  DISCOUNTS_PAGE_LIST: "discounts_page_list",
  DISCOUNTS_PAGE_HERO: "discounts_page_hero",
  DISCOUNTS_PAGE_FEATURED: "discounts_page_featured",
  HOME_LATEST_DISCOUNTS: "home_latest_discounts",
  HOME_FEATURED_FIRMS: "home_featured_firms",
  FIRM_REVIEWS: "firm_reviews",
  HOME_FIRM_LISTING: "home_firm_listing",
  FIRM_LISTING_CARD: "firm_listing_card",
  SAVED_FIRMS_LIST: "saved_firms_list",
  FIRM_DETAIL: "firm_detail",
  COMPARE_CHALLENGES_MODAL: "compare_challenges_modal",
};

/**
 * CTAs that send the visitor to an external site.
 *
 * These are flushed immediately instead of debounced: a new tab can suspend the
 * opener's JavaScript before a queued request leaves.
 */
export const OUTBOUND_CTA_TYPES = new Set([
  CTA_TYPES.USE_OFFER,
  CTA_TYPES.BUY_CHALLENGE,
  CTA_TYPES.PROMO_MODAL_VISIT_SITE,
  CTA_TYPES.VISIT_FIRM_SITE,
]);

const KNOWN_TYPES = new Set(Object.values(CTA_TYPES));
const KNOWN_PLACEMENTS = new Set(Object.values(CTA_PLACEMENTS));

export const isKnownCtaType = (value) => KNOWN_TYPES.has(value);
export const isKnownPlacement = (value) => KNOWN_PLACEMENTS.has(value);
